/* eslint-disable react/destructuring-assignment */
import React, { ChangeEvent, Dispatch, FormEvent, Component } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import console from 'console';
import { UIEvent, PhotoEditorSDKUI, EditorApi } from 'photoeditorsdk';
import _debounce from 'lodash/debounce';
import corsLoad from './corsLoadImage';
import updateAllPadding from '../updateAllPadding';
import ElementPadding from '../../../../componentLibrary/ElementPadding';
import HorizontalRule from '../../../../componentLibrary/HorizontalRule';
import Input from '../../../../componentLibrary/Input';
import InputValidator from '../../../../componentLibrary/InputValidator';
import imageEditorOptions from './imageEditorOptions';
import getImageAspectRatio from '../../../../common/getImageAspectRatio';
import uploadImageAndFetchLink from '../../../../common/uploadImage';
import rules from '../../../../constants/businessRules/businessRules';
import paddingChange from '../../paddingChange';
import eventService from '../../../../common/eventService';
import isTestingEnvironment from '../../../../common/isTestingEnvironment';
import resizeImage from '../../../../common/resizeImage';
import imageSources from '../../../../constants/imageSources/imageSources';
import BananatagButton from '../../../../componentLibrary/BananatagButton';
import Notifier from '../../../../componentLibrary/Notifier';
import * as columnItemActions from '../../../../actions/columnItem';
import * as contentLibraryAction from '../../../../actions/contentLibrary';
import CustomTooltip from '../../../../componentLibrary/CustomTooltip';
import * as Styled from './styles';
import {
    ERR_IMAGE_UPLOAD_FILE_TOO_BIG,
    ERR_IMAGE_UPLOAD_GIF_TOO_BIG,
    ERR_IMAGE_UPLOAD_FILE_TOO_BIG_NO_LINK,
    ERR_IMAGE_UPLOAD_GIF_TOO_BIG_NO_LINK,
    ERR_IMAGE_UPLOAD_RESIZE,
    ERR_IMAGE_UPLOAD_READ,
    ERR_IMAGE_UPLOAD_EDIT,
    ERR_IMAGE_UPLOAD_EDITOR,
} from '../../../../constants/Errors';
import ImageLibraryModal from './ImageLibraryModal/ImageLibraryModal';
import { IImage } from '../../../../types/content';
import { TAppState } from '../../../../reducers';

const _updateConsole = (enable: boolean) => {
    if (enable) {
        window.console = console;
    } else {
        // @ts-ignore
        window.console = {
            log: (_: any) => _,
            error: (_: any) => _,
            warn: (_: any) => _,
            trace: (_: any) => _,
        };
    }
};

const ValidatedLink = InputValidator(Input, { url: 1 });

interface IProps {
    browser: string;
    dispatch: Dispatch<any>;
    hasImageLibraryAccess: boolean;
    IsRawImageUploadEnabled: boolean;
    selectedElementData: {
        blockIndex: string;
        columnIdx: string;
        elmIdx: string;
        content: IImage;
    };
    contentLibraryModal: {
        status: boolean;
        location: number;
    };
    sessionFeatures: { [key: string]: any };
    isPublicPreview: boolean;
}

class ImageSetting extends Component<IProps> {
    consoleCache = {
        log: console.log,
        warn: console.warn,
        error: console.error,
    };

    editor: EditorApi | null = null;

    state = {
        imageEditorVisible: false,
        altInput: this.props.selectedElementData.content.alt,
        hrefInput: this.props.selectedElementData.content.href,
        errorMsg: '',
    };

    debouncedUpdateContent = _debounce(this.updateContent, 250);

    componentDidUpdate(prevProps: IProps) {
        const {
            selectedElementData: { elmIdx },
        } = prevProps;
        const {
            selectedElementData: {
                elmIdx: newElmIdx,
                content: { alt, href },
            },
        } = this.props;

        // when the user clicks on another image element, we need to reset local state so we do not "carry" values from the first element
        if (elmIdx !== newElmIdx) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                altInput: alt,
                hrefInput: href,
            });
        }
    }

    // eslint-disable-next-line react/sort-comp
    async updateContentWithSrc(src: string) {
        try {
            const { blockIndex, columnIdx, elmIdx, content } = this.props.selectedElementData;
            const { dispatch } = this.props;
            const newContentValues = {
                ...content,
                src,
                aspectRatio: await getImageAspectRatio(src),
            };
            const newContent = { ...content, ...newContentValues };
            dispatch(
                columnItemActions.updateColumnItemContent({
                    blockIndex,
                    columnIdx,
                    elmIdx,
                    newContent,
                })
            );
            this.setState((prevState) => ({
                ...prevState,
                contentValues: newContentValues,
            }));
        } catch (err) {
            console.error(err);
            eventService.notify(err, true);
        }
    }

    updateContent(key: string, value: any) {
        const { blockIndex, columnIdx, elmIdx, content } = this.props.selectedElementData;
        const { dispatch } = this.props;
        let newContentValues: IImage;
        if (key === 'updateAllPadding') {
            newContentValues = updateAllPadding(content, value);
        } else {
            newContentValues = { ...content, [key]: value };
        }
        const newContent = { ...content, ...newContentValues };
        dispatch(
            columnItemActions.updateColumnItemContent({
                blockIndex,
                columnIdx,
                elmIdx,
                newContent,
            })
        );
        this.setState((prevState) => ({
            ...prevState,
            contentValues: newContentValues,
        }));
    }

    // for any property other than padding that needs ro run in a batch
    updateMultipleContent(data: any) {
        const {
            selectedElementData: { blockIndex, columnIdx, elmIdx, content },
            dispatch,
        } = this.props;
        let newContentValues = { ...content };
        data.forEach((pair: any) => {
            const { key, value } = pair;
            newContentValues = { ...newContentValues, [key]: value };
        });

        const newContent = { ...content, ...newContentValues };
        dispatch(
            columnItemActions.updateColumnItemContent({
                blockIndex,
                columnIdx,
                elmIdx,
                newContent,
            })
        );
        this.setState((prevState) => ({
            ...prevState,
            contentValues: newContentValues,
        }));
    }

    async uploadImageToBackend(imageData: string) {
        const {
            sessionFeatures: { F_IMAGE_UPLOAD },
        } = this.props;
        try {
            // local only if !F_IMAGE_UPLOAD
            this.updateContentWithSrc(F_IMAGE_UPLOAD ? await uploadImageAndFetchLink({ imageData }) : imageData);
        } catch (err) {
            if (isTestingEnvironment()) {
                const TEMP_UPLOAD_IMAGE =
                    'https://storage-images.bananatag.com/qp94HD/c985d5c8875bf113c617bb009c4409e3.png';
                this.updateContentWithSrc(TEMP_UPLOAD_IMAGE);
            } else {
                console.error(err);
                eventService.notify(err, true);
            }
        }

        // We want close and clean up the editor on save/upload
        if (this.editor && typeof this.editor.dispose === 'function') {
            this.editor.dispose();
            this.editor = null;
        }
        this.setState({ imageEditorVisible: false });
        _updateConsole(true);
    }

    // The following function is a temporary fix for the image editor css that should be removed once the sdk provider implements the solution in the product
    adjustCssForImageEditor = async (container: HTMLElement) => {
        if (!container || !container.children.length) {
            return;
        }
        const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
        await sleep(100);
        const headings = container.querySelectorAll('h1');

        if (headings && headings.length) {
            if (headings[0].innerText.toLowerCase() !== 'text') {
                return;
            }
            const el = container.children[0].children[2].children[1].children[0] as HTMLElement;
            el.style.height = '422px';
            el.style.overflowY = 'auto';
        } else {
            this.adjustCssForImageEditor(document.getElementById('imageEditorContainer') as HTMLElement);
        }
    };

    async initializeImageEditor(imageSrc: string, browser: string) {
        _updateConsole(false);
        try {
            if (this.editor && typeof this.editor.dispose === 'function') {
                // cleaning up old references
                this.editor.dispose();
                this.editor = null;
            }

            const container = document.getElementById('imageEditorContainer');
            const options = await imageEditorOptions(container, imageSrc, browser);
            this.editor = await PhotoEditorSDKUI.init(options);
            this.setState({ imageEditorVisible: true });
            // The next line is a temporary fix for the image editor css that should be removed once the sdk provider implements the solution in the product
            container?.addEventListener('click', () => this.adjustCssForImageEditor(container));
            this.editor.on(UIEvent.EXPORT, (imageData: any) => this.uploadImageToBackend(imageData));
            this.editor.on(UIEvent.CLOSE, () => {
                this.setState({ imageEditorVisible: false });
                // The next line is a temporary fix for the image editor css that should be removed once the sdk provider implements the solution in the product
                container?.removeEventListener('click', () => this.adjustCssForImageEditor(container));
                _updateConsole(true);
            });
        } catch (err) {
            console.error(err);
            eventService.notify(ERR_IMAGE_UPLOAD_EDITOR, true);
        }
    }

    uploadImage = (event: FormEvent<HTMLInputElement>, browser: string) => {
        const { elmIdx } = this.props.selectedElementData;
        const { IsRawImageUploadEnabled } = this.props;
        const imageFile = event.currentTarget.files && event.currentTarget.files[0];
        if (!imageFile) {
            return;
        }

        const isGif = imageFile.type === 'image/gif';
        const sizeRule = isGif ? rules.MAX_GIF_UPLOAD_FILE_SIZE : rules.MAX_IMAGE_UPLOAD_FILE_SIZE;
        if (imageFile.size / 1024 / 1024 > sizeRule) {
            const { isPublicPreview } = this.props;
            if (isPublicPreview) {
                this.setState({
                    errorMsg: isGif ? ERR_IMAGE_UPLOAD_GIF_TOO_BIG_NO_LINK : ERR_IMAGE_UPLOAD_FILE_TOO_BIG_NO_LINK,
                });
            } else {
                eventService.notify(isGif ? ERR_IMAGE_UPLOAD_GIF_TOO_BIG : ERR_IMAGE_UPLOAD_FILE_TOO_BIG, true);
            }
            return;
        }

        const reader = new window.FileReader();
        reader.readAsDataURL(imageFile);
        reader.onload = async () => {
            this.updateContent('mimetype', imageFile.type);

            if (isGif) {
                await this.uploadImageToBackend(reader.result as string);
                return;
            }

            try {
                const imageData = IsRawImageUploadEnabled
                    ? (reader.result as string)
                    : await resizeImage(reader.result as string, imageFile.type);

                const fileUploadInput = document.getElementById(`image-upload-${elmIdx}`) as HTMLInputElement;
                if (fileUploadInput) {
                    fileUploadInput.value = ''; // so that the input onChange event gets fired if user uploads the same image twice
                }
                await this.initializeImageEditor(imageData, browser);
            } catch (err) {
                console.error(err);
                eventService.notify(ERR_IMAGE_UPLOAD_RESIZE, true);
            }
        };
        reader.onerror = (err) => {
            console.error(err);
            eventService.notify(ERR_IMAGE_UPLOAD_READ, true);
        };
    };

    renderButtons = (
        hasIntegrationFeature: boolean,
        elmIdx: string,
        hasImageLibraryAccess: boolean,
        browser: string,
        dispatch: Dispatch<any>
    ) => {
        if (!hasIntegrationFeature) {
            return (
                <>
                    <BananatagButton
                        file
                        size="small"
                        index={`image-upload-${elmIdx}`}
                        text="Upload new image"
                        allowGifs
                        onChange={(e) => this.uploadImage(e, browser)}
                        styling={{
                            width: '100%',
                            display: 'block',
                            marginBottom: 10,
                        }}
                    />
                    <div data-tip data-for="disabledSharedLibrary" style={{ cursor: 'not-allowed' }}>
                        <BananatagButton
                            size="small"
                            type="outline"
                            text="Open Shared Library"
                            styling={{
                                width: '180px',
                                marginBottom: 10,
                                display: 'block',
                                cursor: 'not-allowed',
                            }}
                        />
                    </div>
                    {/* @ts-ignore - missing icon */}
                    <CustomTooltip
                        dataRef="disabledSharedLibrary"
                        offset={{ top: 6 }}
                        effect="float"
                        text="Create a library of images to share with your team. Learn more by booking a full product demo."
                    />
                </>
            );
        }
        if (hasImageLibraryAccess && browser !== 'ie') {
            return (
                <>
                    <BananatagButton
                        size="small"
                        text="Upload Image/Gif"
                        onClick={() => {
                            dispatch(contentLibraryAction.setContentLibraryTab('upload'));
                        }}
                        styling={{
                            width: '100%',
                            display: 'block',
                            marginBottom: 10,
                        }}
                    />
                    <BananatagButton
                        size="small"
                        type="outline"
                        index={`image-upload-${elmIdx}`}
                        text="Open Shared Library"
                        onClick={() => {
                            dispatch(contentLibraryAction.setFolders('library'));
                        }}
                        styling={{
                            width: '100%',
                            marginBottom: 10,
                            display: 'block',
                            cursor: 'pointer',
                        }}
                    />
                </>
            );
        }
        return (
            <BananatagButton
                file
                size="small"
                index={`image-upload-${elmIdx}`}
                icon="cloud_upload"
                text="Upload new image"
                allowGifs
                onChange={(e) => this.uploadImage(e, browser)}
                styling={{ width: '100%', marginBottom: 10 }}
            />
        );
    };

    render() {
        const { imageEditorVisible, altInput, hrefInput, errorMsg } = this.state;
        const {
            selectedElementData,
            dispatch,
            hasImageLibraryAccess,
            contentLibraryModal,
            browser,
            sessionFeatures,
            isPublicPreview,
        } = this.props;
        const {
            elmIdx,
            content: { src, mimetype, href, alt, paddingTop, paddingRight, paddingBottom, paddingLeft },
        } = selectedElementData;
        const { F_IMAGE_LIBRARY } = sessionFeatures;

        return (
            <div>
                {/* @ts-ignore */}
                <Styled.ImageEditorContainer imageEditorVisible={imageEditorVisible}>
                    <Styled.ImageEditorDiv id="imageEditorContainer" />
                </Styled.ImageEditorContainer>

                {contentLibraryModal.status && <ImageLibraryModal selectedElementData={selectedElementData} />}

                <Styled.ImageSettingRow>
                    <HorizontalRule />
                    <ElementPadding
                        paddingTop={paddingTop}
                        paddingBottom={paddingBottom}
                        paddingLeft={paddingLeft}
                        paddingRight={paddingRight}
                        elementType="image"
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            const payload = paddingChange({
                                event,
                                paddingBottom,
                                paddingLeft,
                                paddingRight,
                                paddingTop,
                            });
                            if (!payload) {
                                return;
                            }
                            const { key, value = 0 } = payload;
                            this.updateContent(key, value);
                        }}
                    />
                    <HorizontalRule />
                    {this.renderButtons(F_IMAGE_LIBRARY, elmIdx, hasImageLibraryAccess, browser, dispatch)}
                    <Styled.LinkTipDiv data-test-id="adjust-HC-link">
                        <Styled.Icon icon="external-link" />
                        <Styled.Link
                            href="https://support.staffbase.com/hc/en-us/articles/4412592738194"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            How to resize images/GIFs
                        </Styled.Link>
                    </Styled.LinkTipDiv>
                    {src !== 'PLACE_HOLDER' && (
                        <React.Fragment>
                            <Styled.ButtonDiv>
                                {mimetype !== 'image/gif' && (
                                    <BananatagButton
                                        size="small"
                                        styling={{
                                            width: 85,
                                            display: 'block',
                                        }}
                                        text="edit"
                                        type="outline"
                                        onClick={() => {
                                            const {
                                                sessionFeatures: { F_IMAGE_UPLOAD },
                                            } = this.props;
                                            if (!F_IMAGE_UPLOAD && src.startsWith('data:image')) {
                                                this.initializeImageEditor(src, browser);
                                            } else {
                                                corsLoad(src, (err: Error, imageData: any) => {
                                                    if (err) {
                                                        console.error(err);
                                                        eventService.notify(ERR_IMAGE_UPLOAD_EDIT, true);
                                                        return;
                                                    }
                                                    this.initializeImageEditor(imageData, browser);
                                                });
                                            }
                                        }}
                                    />
                                )}
                                <BananatagButton
                                    size="small"
                                    styling={{
                                        width: mimetype !== 'image/gif' ? 91 : 180,
                                        display: 'block',
                                    }}
                                    text="remove"
                                    type="outline"
                                    onClick={() => {
                                        this.updateMultipleContent([
                                            {
                                                key: 'src',
                                                value: 'PLACE_HOLDER',
                                            },
                                            { key: 'aspectRatio', value: null },
                                        ]);
                                    }}
                                    disabled={src === 'PLACE_HOLDER'}
                                />
                            </Styled.ButtonDiv>
                            <ValidatedLink
                                type="text"
                                width={180}
                                icon="link"
                                elmIdx={elmIdx}
                                value={hrefInput}
                                placeholder={href.length > 0 ? href : 'Image Target Link'}
                                label="IMAGE REDIRECT"
                                marginBottom="0"
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    const { value } = event.target;
                                    if (value === hrefInput) {
                                        return;
                                    }
                                    this.setState({ hrefInput: value }, () => {
                                        const { hrefInput: updatedHrefInput } = this.state;
                                        this.debouncedUpdateContent('href', updatedHrefInput);
                                    });
                                }}
                            />
                            <Input
                                type="text"
                                width={180}
                                maxLength={125}
                                customIcon={imageSources.ICONS.ALT_TEXT}
                                label="ALTERNATIVE TEXT"
                                id="image-setting-alt-text"
                                placeholder={alt.length > 0 ? alt : 'Description of image...'}
                                value={altInput}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    const { value } = event.target;
                                    if (value === altInput) {
                                        return;
                                    }
                                    this.setState({ altInput: value }, () => {
                                        const { altInput: updatedAltInput } = this.state;
                                        this.debouncedUpdateContent('alt', updatedAltInput);
                                    });
                                }}
                            />
                        </React.Fragment>
                    )}
                </Styled.ImageSettingRow>
                {isPublicPreview && errorMsg && <Notifier type="ERROR" message={errorMsg} />}
            </div>
        );
    }
}

const mapStateToProps = (state: TAppState) => ({
    browser: _get(state, 'browser.browserName', 'CANNOT_DETECT'),
    contentLibraryModal: _get(state, 'modals.contentLibraryModal', ''),
    hasImageLibraryAccess: _get(state, 'session.user.features.CONTENT_LIBRARY_ACCESS', false),
    IsRawImageUploadEnabled: _get(state, 'session.user.features.EMAIL_DESIGNER_RAW_IMAGE_UPLOA', false),
    isPublicPreview: _get(state, 'session.integration', '') === 'PUBLIC_PREVIEW',
    sessionFeatures: _get(state, 'session.features', {}),
});

export default connect(mapStateToProps)(ImageSetting);
